import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  ReservationReturn,
  ReservationRuleCode,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { reservationService } from '../clients/ApiClients';
import { formatMoney } from '../utils/billingHelpers';
import { RuleViolationsPanel } from './RuleViolationsPanel';
import {
  canSubmitOverrides,
  formatLocalRange,
  isValidFee,
  isZeroMoney,
  reservationErrorMessage,
  ruleViolationsFrom,
} from './reservationMeta';
import { checkboxClass, inputClass, labelClass } from './formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * The decisions a manager makes on a booking, as dialogs — approve, deny,
 * cancel, and (P2) mark a no-show. Each resolves with the reservation as
 * the server now has it, so the caller can replace its copy — the server
 * is the only thing that knows whether a fee posted, was reversed, or has
 * to be refunded by hand.
 */

export type Decision = 'approve' | 'deny' | 'cancel' | 'no_show';

interface Props {
  reservation: ReservationReturn;
  decision: Decision | null;
  onClose: () => void;
  onDecided: (updated: ReservationReturn) => void;
}

const summary = (r: ReservationReturn) =>
  `${r.amenity_name} · ${r.home_address} · ${formatLocalRange(r.start_local, r.end_local)}`;

export const ReservationDecisionDialogs: React.FC<Props> = ({
  reservation,
  decision,
  onClose,
  onDecided,
}) => (
  <>
    <ApproveDialog
      open={decision === 'approve'}
      reservation={reservation}
      onClose={onClose}
      onDecided={onDecided}
    />
    <DenyDialog
      open={decision === 'deny'}
      reservation={reservation}
      onClose={onClose}
      onDecided={onDecided}
    />
    <CancelDialog
      open={decision === 'cancel'}
      reservation={reservation}
      onClose={onClose}
      onDecided={onDecided}
    />
    <NoShowDialog
      open={decision === 'no_show'}
      reservation={reservation}
      onClose={onClose}
      onDecided={onDecided}
    />
  </>
);

interface DialogProps {
  open: boolean;
  reservation: ReservationReturn;
  onClose: () => void;
  onDecided: (updated: ReservationReturn) => void;
}

/**
 * Approve: the fee posts to the home's account now. The manager may
 * change the fee (with a reason), leave a note the household sees, and —
 * if the booking breaks a rule it did not break when requested, like a
 * balance that has since gone past due — override those rules one by one.
 */
const ApproveDialog: React.FC<DialogProps> = ({ open, reservation: r, onClose, onDecided }) => {
  const [changeFee, setChangeFee] = useState(false);
  const [fee, setFee] = useState(r.fee_amount);
  const [feeReason, setFeeReason] = useState('');
  const [note, setNote] = useState('');
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const [ticked, setTicked] = useState<string[]>([]);
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChangeFee(false);
    setFee(r.fee_amount);
    setFeeReason('');
    setNote('');
    setViolations([]);
    setTicked([]);
    setOverrideReason('');
    setError(null);
  }, [open, r.fee_amount]);

  const feeOk = !changeFee || (isValidFee(fee) && feeReason.trim().length > 0);
  const overridesOk = canSubmitOverrides(violations, ticked, overrideReason);
  const canSubmit = !saving && feeOk && overridesOk;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.approveReservation({
        reservationId: r.id,
        reservationApprove: {
          decision_note: note.trim() || null,
          fee_override: changeFee ? fee.trim() : null,
          reason: changeFee ? feeReason.trim() : null,
          overrides: ticked.length ? (ticked as ReservationRuleCode[]) : null,
          override_reason: ticked.length ? overrideReason.trim() : null,
        },
      });
      onDecided(res.data);
      onClose();
    } catch (err) {
      const broken = ruleViolationsFrom(err);
      if (broken) {
        setViolations(broken);
        setTicked((t) => t.filter((c) => broken.some((v) => v.code === c)));
        setError(null);
      } else {
        setError(reservationErrorMessage(err, "Couldn't approve the reservation."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Approve reservation</DialogTitle>
        <DialogDescription>{summary(r)}</DialogDescription>
        <DialogBody className="space-y-4">
          <p className="text-[13px] text-fg-secondary">
            {isZeroMoney(changeFee ? fee : r.fee_amount)
              ? 'There is no fee. The household is told the booking is confirmed.'
              : `${formatMoney(changeFee ? fee : r.fee_amount)} is charged to the home's account when you approve, due on the day of use.`}
          </p>
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={changeFee}
              onChange={(e) => setChangeFee(e.target.checked)}
            />
            Change the fee for this booking
          </label>
          {changeFee && (
            <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
              <label className={labelClass}>
                Fee
                <span className={clsx(inputClass, 'flex items-center', !isValidFee(fee) && 'border-danger')}>
                  <span className="text-fg-muted">$</span>
                  <input
                    inputMode="decimal"
                    value={fee}
                    onChange={(e) => setFee(e.target.value.replace(/[^\d.]/g, ''))}
                    aria-invalid={!isValidFee(fee)}
                    className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
                  />
                </span>
              </label>
              <label className={labelClass}>
                Why (required)
                <input
                  value={feeReason}
                  onChange={(e) => setFeeReason(e.target.value)}
                  placeholder="e.g. Waived for the HOA's own event"
                  className={inputClass}
                />
              </label>
            </div>
          )}
          <label className={labelClass}>
            Note to the household (optional)
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. The key is with the front desk."
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <RuleViolationsPanel
            idPrefix="approve"
            violations={violations}
            ticked={ticked}
            onTickedChange={setTicked}
            reason={overrideReason}
            onReasonChange={setOverrideReason}
          />
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!canSubmit}>
            {saving ? 'Approving…' : violations.length ? 'Approve with overrides' : 'Approve'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

/** Deny: a pending request only. Nothing was charged, so nothing to reverse. */
const DenyDialog: React.FC<DialogProps> = ({ open, reservation: r, onClose, onDecided }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.denyReservation({
        reservationId: r.id,
        reservationDeny: { reason: reason.trim() },
      });
      onDecided(res.data);
      onClose();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't deny the request."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Deny request</DialogTitle>
        <DialogDescription>{summary(r)}</DialogDescription>
        <DialogBody className="space-y-4">
          <p className="text-[13px] text-fg-secondary">
            The slot is released and nothing is charged. The household is told, with your reason.
          </p>
          <label className={labelClass}>
            Reason (required — the household sees it)
            <textarea
              rows={3}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Keep request
          </Button>
          <Button color="red" type="submit" disabled={saving || !reason.trim()}>
            {saving ? 'Denying…' : 'Deny request'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

/**
 * Cancel: pending or approved. A posted fee is reversed unless the
 * manager says to keep it — and one the household has already paid can
 * never be reversed automatically; it becomes "refund due" for the
 * existing refund process, like a paid fine.
 */
const CancelDialog: React.FC<DialogProps> = ({ open, reservation: r, onClose, onDecided }) => {
  const [reason, setReason] = useState('');
  const [reverseFee, setReverseFee] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setReverseFee(true);
      setError(null);
    }
  }, [open]);

  const feePosted = r.fee_status === 'posted';
  const outcome = r.cancel_outcome_now?.outcome;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.cancelReservation({
        reservationId: r.id,
        reservationCancel: { reason: reason.trim(), reverse_fee: feePosted ? reverseFee : null },
      });
      onDecided(res.data);
      onClose();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't cancel the reservation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Cancel reservation</DialogTitle>
        <DialogDescription>{summary(r)}</DialogDescription>
        <DialogBody className="space-y-4">
          {feePosted ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={reverseFee}
                  onChange={(e) => setReverseFee(e.target.checked)}
                />
                Reverse the {formatMoney(r.fee_amount)} fee
              </label>
              <p className="text-[12px] text-fg-muted">
                {reverseFee
                  ? outcome === 'returned'
                    ? 'This fee has already been paid, so it is returned to the home’s account — refunded by cheque, or left as a credit if the association allows credit balances.'
                    : 'A reversal is posted to the home’s account. If the fee has already been paid it is returned instead — refunded by cheque, or left as a credit if the association allows credit balances.'
                  : 'The fee stays on the home’s account, paid or not.'}
              </p>
            </div>
          ) : (
            <p className="text-[13px] text-fg-secondary">
              {r.status === 'pending'
                ? 'Nothing has been charged for this request.'
                : 'There is no fee on the account to reverse.'}
            </p>
          )}
          <label className={labelClass}>
            Reason (required — the household sees it)
            <textarea
              rows={3}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Keep reservation
          </Button>
          <Button color="red" type="submit" disabled={saving || !reason.trim()}>
            {saving ? 'Cancelling…' : 'Cancel reservation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

/**
 * No-show (P2): the household booked and did not come. Only after the
 * booking has started, and only once. The fee is untouched — it was for
 * the slot, which was held — and the mark counts on the utilization
 * report.
 */
const NoShowDialog: React.FC<DialogProps> = ({ open, reservation: r, onClose, onDecided }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.markReservationNoShow({
        reservationId: r.id,
        reservationNoShow: { reason: reason.trim() },
      });
      onDecided(res.data);
      onClose();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't mark the no-show."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Mark as a no-show</DialogTitle>
        <DialogDescription>{summary(r)}</DialogDescription>
        <DialogBody className="space-y-4">
          <p className="text-[13px] text-fg-secondary">
            The household booked and did not use it. Any fee stays as it is. No-shows are counted on the
            utilization report.
          </p>
          <label className={labelClass}>
            What happened (required — recorded on the history)
            <textarea
              rows={3}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Court empty at 6:15 PM; no call ahead."
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="amber" type="submit" disabled={saving || !reason.trim()}>
            {saving ? 'Saving…' : 'Mark no-show'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReservationDecisionDialogs;
